import { useEffect, useState } from "react";
import { ArrowUpRight, Check, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BaseSheet } from "@/components/common/BaseSheet";
import { TranscriptCopyButton } from "@/components/conversation/details/TranscriptCopyButton";
import { Button } from "@/components/ui/button";
import {
  useAddMeetingToKnowledge,
  useDeleteGenieMeeting,
  useForceStopGenieMeeting,
  useGenieMeeting,
} from "@/hooks/agent/useGenieMeetings";
import {
  canForceStop,
  describeMeetingFailure,
  formatElapsed,
  formatMeetingDuration,
  formatMeetingMoment,
  formatTranscriptTime,
  isLiveMeeting,
  isMeetingInProgress,
  meetingMoment,
  PROVIDER_LABELS,
  STATE_LABELS,
  toConversationTranscript,
} from "@/lib/agent/genieMeetings";
import {
  createNotesDraft,
  draftToParts,
  hasDraftContent,
  MeetingKnowledgeReview,
  MeetingNotesDraft,
} from "./MeetingKnowledgeReview";
import { MeetingActivity } from "./MeetingActivity";
import { Agent } from "@/types/agent/agent";
import { GenieMeetingDetail } from "@/types/agent/genie-meeting";

interface MeetingDetailsDrawerProps {
  genie: Agent;
  meetingId: string | null;
  onClose: () => void;
}

const SECTION_LABEL = "text-sm font-semibold text-gray-700 mb-2";
const ELAPSED_TICK_MS = 15_000;

const describeMeeting = (meeting: GenieMeetingDetail): string =>
  [
    PROVIDER_LABELS[meeting.meeting_provider],
    formatMeetingMoment(meetingMoment(meeting)),
    formatMeetingDuration(meeting.duration_secs),
  ]
    .filter(Boolean)
    .join(" · ");

const pendingMessage = (meeting: GenieMeetingDetail, genieName: string): string => {
  switch (meeting.state) {
    case "scheduled":
      return `${genieName} will join this meeting at ${formatMeetingMoment(meetingMoment(meeting))}.`;
    case "joining":
      return `${genieName} is on its way into the meeting.`;
    case "waiting":
      return `${genieName} is in the lobby. Someone in the meeting needs to let it in.`;
    case "in_meeting":
      return `${genieName} is in the meeting, taking notes. They will appear here when it ends.`;
    case "processing":
      return `The meeting is over. ${genieName} is writing up the notes, which usually takes a few minutes.`;
    case "cancelled":
      return "This meeting was cancelled.";
    case "failed":
      return describeMeetingFailure(meeting.failure_reason);
    default:
      return "";
  }
};

export const MeetingDetailsDrawer = ({ genie, meetingId, onClose }: MeetingDetailsDrawerProps) => {
  const { data: meeting, isLoading, isError } = useGenieMeeting(meetingId ?? undefined);
  const addToKnowledge = useAddMeetingToKnowledge(genie.id);
  const deleteMeeting = useDeleteGenieMeeting(genie.id);
  const forceStop = useForceStopGenieMeeting(genie.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingForceStop, setConfirmingForceStop] = useState(false);
  const [draft, setDraft] = useState<MeetingNotesDraft | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const inProgress = !!meeting && isMeetingInProgress(meeting);

  useEffect(() => {
    setConfirmingDelete(false);
    setConfirmingForceStop(false);
    setDraft(null);
  }, [meetingId]);

  // Keeps "in this step for …" current while the genie is joining, in, or wrapping up a meeting.
  useEffect(() => {
    if (!inProgress) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
    return () => clearInterval(timer);
  }, [inProgress]);

  const handleAddToKnowledge = async () => {
    if (!meeting || !draft) return;
    try {
      await addToKnowledge.mutateAsync({ meetingId: meeting.id, parts: draftToParts(draft) });
      toast.success(`${genie.name} is learning from these notes`);
      setDraft(null);
    } catch (err: any) {
      toast.error(err.message ?? "These notes couldn't be added to the knowledge base");
    }
  };

  const handleDelete = async () => {
    if (!meeting) return;
    try {
      await deleteMeeting.mutateAsync(meeting.id);
      toast.success("Meeting deleted");
      onClose();
    } catch (err: any) {
      toast.error(err.message ?? "This meeting couldn't be deleted");
    }
  };

  const handleForceStop = async () => {
    if (!meeting) return;
    try {
      await forceStop.mutateAsync(meeting.id);
      toast.success(`${genie.name} was stopped`);
      setConfirmingForceStop(false);
    } catch (err: any) {
      toast.error(err.message ?? `${genie.name} couldn't be stopped`);
    }
  };

  const isCompleted = meeting?.state === "completed";
  const events = meeting?.events ?? [];
  const forceStoppable = !!meeting && canForceStop(meeting, events, now);
  const stepStartedAt = meeting ? (events[events.length - 1]?.created_at ?? meeting.updated_at) : null;
  const activity = meeting && <MeetingActivity events={events} genieName={genie.name} />;
  const hasNotes = !!meeting && (!!meeting.summary || meeting.transcript.length > 0);

  const primaryButtonClass =
    "min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg";

  const forceStopFooter = forceStoppable && (
    <div className="w-full flex flex-wrap items-center justify-start gap-3">
      {confirmingForceStop ? (
        <>
          <span className="text-sm text-gray-600">Stop {genie.name} for good? Nothing it recorded will be kept.</span>
          <Button
            variant="outline"
            onClick={handleForceStop}
            disabled={forceStop.isPending}
            className="min-h-[44px] text-red-600 border-red-200 hover:bg-red-50"
          >
            {forceStop.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Yes, force stop
          </Button>
          <Button variant="outline" onClick={() => setConfirmingForceStop(false)} className="min-h-[44px]">
            Keep waiting
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          onClick={() => setConfirmingForceStop(true)}
          className="min-h-[44px] text-red-600 border-red-200 hover:bg-red-50"
        >
          Force stop
        </Button>
      )}
    </div>
  );

  const footer = meeting && isLiveMeeting(meeting) ? forceStopFooter : meeting && (
    <div className="w-full flex flex-wrap items-center justify-start gap-3">
      {draft ? (
        <>
          <Button
            onClick={handleAddToKnowledge}
            disabled={addToKnowledge.isPending || !hasDraftContent(draft)}
            className={primaryButtonClass}
          >
            {addToKnowledge.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {addToKnowledge.isPending ? "Adding..." : "Add to knowledge"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setDraft(null)}
            disabled={addToKnowledge.isPending}
            className="min-h-[44px]"
          >
            Back
          </Button>
        </>
      ) : confirmingDelete ? (
        <>
          <span className="text-sm text-gray-600">Delete this meeting and its notes for good?</span>
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleteMeeting.isPending}
            className="min-h-[44px] text-red-600 border-red-200 hover:bg-red-50"
          >
            {deleteMeeting.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Yes, delete
          </Button>
          <Button variant="outline" onClick={() => setConfirmingDelete(false)} className="min-h-[44px]">
            Keep
          </Button>
        </>
      ) : (
        <>
          {isCompleted && hasNotes && !meeting.document_id && (
            <Button onClick={() => setDraft(createNotesDraft(meeting))} className={primaryButtonClass}>
              Add to knowledge
            </Button>
          )}
          <Button variant="outline" onClick={() => setConfirmingDelete(true)} className="min-h-[44px]">
            Delete
          </Button>
        </>
      )}
    </div>
  );

  return (
    <BaseSheet
      isOpen={!!meetingId}
      onClose={onClose}
      title={meeting?.title || "Meeting notes"}
      description={meeting ? describeMeeting(meeting) : `Notes ${genie.name} took in this meeting`}
      size="lg"
      footer={footer || undefined}
    >
      {isLoading && (
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
          <span className="text-sm text-gray-600">Fetching the notes...</span>
        </div>
      )}

      {isError && <p className="text-sm text-red-600">These notes couldn't be loaded. Close this and try again.</p>}

      {meeting && draft && (
        <MeetingKnowledgeReview meeting={meeting} genieName={genie.name} draft={draft} onChange={setDraft} />
      )}

      {meeting && !draft && (
        <div className="space-y-6">
          <section>
            <h3 className={SECTION_LABEL}>Meeting link</h3>
            <a
              href={meeting.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 min-h-[44px] text-sm text-genie-primary hover:underline break-all"
            >
              {meeting.meeting_link}
              <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
            </a>
          </section>

          {!isCompleted && (
            <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="rounded-[10px] bg-white p-5">
                <span className="font-semibold text-gray-900">{STATE_LABELS[meeting.state]}</span>
                <p className="text-sm text-gray-600 mt-2">{pendingMessage(meeting, genie.name)}</p>
                {inProgress && stepStartedAt && (
                  <p className="text-xs text-gray-500 mt-2">In this step for {formatElapsed(stepStartedAt, now)}</p>
                )}
                {forceStoppable && (
                  <p className="text-sm text-gray-600 mt-2">
                    {genie.name} was asked to leave but still shows as in the meeting. If nobody is left in it, use
                    Force stop to remove {genie.name} for good.
                  </p>
                )}
              </div>
            </div>
          )}

          {!isCompleted && activity}

          {isCompleted && !hasNotes && (
            <p className="text-sm text-gray-600">
              {genie.name} joined, but nothing was said that it could take notes on.
            </p>
          )}

          {meeting.summary && (
            <section>
              <h3 className={SECTION_LABEL}>Summary</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{meeting.summary}</p>
            </section>
          )}

          {meeting.action_items.length > 0 && (
            <section>
              <h3 className={SECTION_LABEL}>Action items</h3>
              <ul className="space-y-2">
                {meeting.action_items.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-genie-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {isCompleted && hasNotes && (
            <section>
              <h3 className={SECTION_LABEL}>Knowledge base</h3>
              {meeting.document_id ? (
                <p className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 shrink-0 text-genie-primary" />
                  These notes are in {genie.name}'s knowledge base.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  {genie.name} shares what it knows with your customers. You choose which parts of these notes go in,
                  and can edit out anything that should stay internal.
                </p>
              )}
            </section>
          )}

          {meeting.conversation_id && (
            <Link
              to={`/genie-hub/conversations/${meeting.conversation_id}`}
              className="inline-flex items-center gap-1 min-h-[44px] text-sm text-genie-primary hover:underline"
            >
              Open the full analysis in the Inbox
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}

          {meeting.transcript.length > 0 && (
            <section>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Transcript</h3>
                <TranscriptCopyButton
                  rawTranscript={toConversationTranscript(meeting.transcript)}
                  genieName={genie.name}
                  dateRaw={meetingMoment(meeting)}
                  durationSeconds={meeting.duration_secs}
                  isMeeting
                />
              </div>
              <div className="space-y-3">
                {meeting.transcript.map((segment, index) => (
                  <div key={index} className="text-sm">
                    {(segment.speaker || segment.start_ms !== null) && (
                      <div className="flex items-baseline gap-2">
                        {segment.speaker && <span className="font-semibold text-gray-900">{segment.speaker}</span>}
                        {segment.start_ms !== null && (
                          <span className="text-xs text-gray-500">{formatTranscriptTime(segment.start_ms)}</span>
                        )}
                      </div>
                    )}
                    <p className="text-gray-600 leading-relaxed">{segment.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {isCompleted && activity}
        </div>
      )}
    </BaseSheet>
  );
};

export default MeetingDetailsDrawer;
